import { Box, Button, Chip, CircularProgress, Collapse, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useRef, useState } from 'react';
import type { OcrAct, OcrZone, OcrZoneType } from '../api/ocr';

const ZONE_COLOURS: Record<OcrZoneType, string> = {
    target_record: '#3b82f6',
    other_record: '#9ca3af',
    marginal_note: '#f59e0b',
    header: '#a78bfa',
    signatures: '#10b981',
    other: '#f472b6',
};

const ZONE_LABELS: Record<OcrZoneType, string> = {
    target_record: 'Acte',
    other_record: 'Autre acte',
    marginal_note: 'Mention marginale',
    header: 'En-tête',
    signatures: 'Signatures',
    other: 'Autre',
};

// Reading order in the panel: the record the act is about first.
const ZONE_ORDER: OcrZoneType[] = ['target_record', 'marginal_note', 'other_record', 'header', 'signatures', 'other'];

const LEGIBILITY_LABELS: Record<string, string> = { good: 'bonne lisibilité', partial: 'lisibilité partielle', poor: 'peu lisible' };

const LANGUAGE_LABELS: Record<string, string> = { fr: 'français', es: 'espagnol', en: 'anglais', de: 'allemand' };

const zoneColour = (zone: OcrZone) => ZONE_COLOURS[zone.type] ?? ZONE_COLOURS.other;

/** Zone boxes drawn over an image; positioned in % so they follow zoom and pan. */
export function OcrZoneOverlay({ zones, activeZone, onHoverZone }: {
    zones: OcrZone[];
    activeZone: number | null;
    onHoverZone: (index: number | null) => void;
}) {
    return (
        <>
            {zones.map((zone, index) => {
                if (zone.bbox.length !== 4) return null;
                const [x0, y0, x1, y1] = zone.bbox.map((v) => Math.min(1000, Math.max(0, v)) / 10);
                const active = activeZone === index;
                return (
                    <div
                        key={index}
                        onMouseEnter={() => onHoverZone(index)}
                        onMouseLeave={() => onHoverZone(null)}
                        onClick={(e) => {
                            e.stopPropagation();
                            onHoverZone(active ? null : index);
                        }}
                        style={{
                            position: 'absolute',
                            left: `${x0}%`,
                            top: `${y0}%`,
                            width: `${Math.max(x1 - x0, 0.5)}%`,
                            height: `${Math.max(y1 - y0, 0.5)}%`,
                            border: `2px solid ${zoneColour(zone)}`,
                            background: active ? `${zoneColour(zone)}33` : 'transparent',
                            boxShadow: active ? `0 0 0 3px ${zoneColour(zone)}` : undefined,
                            boxSizing: 'border-box',
                            borderRadius: 2,
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                        }}
                    />
                );
            })}
        </>
    );
}

// The slider listens to pointer and wheel events to swipe and zoom; the panel
// intercepts them so its text can be scrolled and selected without triggering
// PhotoSlider's photo dragging/swiping.
const stopSliderGestures = {
    onPointerDown: (e: React.SyntheticEvent) => e.stopPropagation(),
    onPointerMove: (e: React.SyntheticEvent) => e.stopPropagation(),
    onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
    onTouchMove: (e: React.SyntheticEvent) => e.stopPropagation(),
    onTouchEnd: (e: React.SyntheticEvent) => e.stopPropagation(),
    onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
    onWheel: (e: React.SyntheticEvent) => e.stopPropagation(),
};

export type MobileSheetHeight = 'peek' | 'half' | 'full';

/** Transcription of the media shown in the slider, zone by zone. */
export function OcrPanel({ acts, loading, activeZone, onHoverZone, isMobile, onClose }: {
    acts: OcrAct[];
    loading: boolean;
    activeZone: number | null;
    onHoverZone: (index: number | null) => void;
    isMobile: boolean;
    onClose?: () => void;
}) {
    const [fullTextOpen, setFullTextOpen] = useState(false);
    // The translation is shown first when there is one; the original stays one click away.
    const [showOriginal, setShowOriginal] = useState(false);
    // On mobile: peek (bottom bar), half (50vh split), or full (88vh reading sheet)
    const [mobileHeight, setMobileHeight] = useState<MobileSheetHeight>('half');
    const panelRef = useRef<HTMLDivElement>(null);

    // Stop native touch events from bubbling to react-photo-view's global window
    // listener, which calls e.preventDefault() on touchmove and freezes native scrolling.
    useEffect(() => {
        const el = panelRef.current;
        if (!el) return;
        const stopTouch = (e: TouchEvent) => {
            e.stopPropagation();
        };
        el.addEventListener('touchstart', stopTouch, { passive: true });
        el.addEventListener('touchmove', stopTouch, { passive: true });
        el.addEventListener('touchend', stopTouch, { passive: true });
        return () => {
            el.removeEventListener('touchstart', stopTouch);
            el.removeEventListener('touchmove', stopTouch);
            el.removeEventListener('touchend', stopTouch);
        };
    }, []);

    const act = acts[0];
    const translation = act?.translation ?? null;
    const translated = Boolean(translation) && !showOriginal;
    const zoneText = (zone: OcrZone, index: number) => (translated && translation?.zones[String(index)]) || zone.text;
    const fullText = translated && translation?.target_text ? translation.target_text : act?.target_text;
    // Zones keep their index in the result, which the image overlay uses too.
    const orderedZones = (act?.page.zones ?? [])
        .map((zone, index) => ({ zone, index }))
        .sort((a, b) => ZONE_ORDER.indexOf(a.zone.type) - ZONE_ORDER.indexOf(b.zone.type));

    const cycleHeight = () => {
        setMobileHeight((current) => {
            if (current === 'peek') return 'half';
            if (current === 'half') return 'full';
            return 'half';
        });
    };

    return (
        <Box
            ref={panelRef}
            {...stopSliderGestures}
            sx={{
                position: 'fixed',
                // Above the slider's photo layer (10), below its top banner (20).
                zIndex: 15,
                color: 'grey.100',
                bgcolor: 'rgba(18, 18, 20, 0.94)',
                backdropFilter: 'blur(16px)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'height 0.25s ease-out, max-height 0.25s ease-out',
                touchAction: 'pan-y',
                boxShadow: isMobile ? '0 -6px 28px rgba(0, 0, 0, 0.6)' : '0 4px 20px rgba(0, 0, 0, 0.5)',
                borderTop: isMobile ? '1px solid rgba(255, 255, 255, 0.12)' : undefined,
                ...(isMobile
                    ? {
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderTopLeftRadius: 16,
                          borderTopRightRadius: 16,
                          height: mobileHeight === 'peek' ? 'auto' : mobileHeight === 'half' ? '50vh' : '88vh',
                          maxHeight: mobileHeight === 'peek' ? '140px' : mobileHeight === 'half' ? '50vh' : '88vh',
                      }
                    : {
                          top: 64,
                          right: 16,
                          bottom: 64,
                          width: 'min(420px, 38vw)',
                          borderRadius: 2,
                          maxHeight: 'calc(100vh - 128px)',
                      }),
            }}
        >
            {/* Drag pill handle on mobile */}
            {isMobile && (
                <Box
                    onClick={cycleHeight}
                    sx={{
                        pt: 1,
                        pb: 0.5,
                        display: 'flex',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <Box sx={{ width: 38, height: 4, borderRadius: 2, bgcolor: 'grey.600' }} />
                </Box>
            )}

            {/* Header row with status, title & height controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: isMobile ? 0.5 : 2, pb: 1, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                    <Typography variant="caption" sx={{ color: 'grey.400', display: 'block', fontWeight: 500, lineHeight: 1.2 }}>
                        Transcription automatique {act && act.pages.length > 1 ? `· page ${act.page.image} / ${act.pages.length}` : ''}
                        {loading && ' (chargement...)'}
                    </Typography>
                    {isMobile && mobileHeight === 'peek' && act?.summary_fr && (
                        <Typography variant="body2" noWrap sx={{ fontStyle: 'italic', color: 'grey.300', mt: 0.25 }}>
                            {act.summary_fr}
                        </Typography>
                    )}
                </Box>

                {/* Control buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isMobile && (
                        <>
                            {mobileHeight === 'peek' ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setMobileHeight('half')}
                                    startIcon={<KeyboardArrowUpIcon fontSize="small" />}
                                    sx={{ color: 'grey.200', borderColor: 'grey.700', py: 0.25, px: 1, fontSize: 12, textTransform: 'none' }}
                                >
                                    Voir ({orderedZones.length})
                                </Button>
                            ) : (
                                <>
                                    {mobileHeight === 'half' && (
                                        <Tooltip title="Plein écran">
                                            <IconButton size="small" onClick={() => setMobileHeight('full')} sx={{ color: 'grey.300', p: 0.5 }}>
                                                <KeyboardArrowUpIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={mobileHeight === 'full' ? 'Réduire' : 'Replier'}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setMobileHeight(mobileHeight === 'full' ? 'half' : 'peek')}
                                            sx={{ color: 'grey.300', p: 0.5 }}
                                        >
                                            <KeyboardArrowDownIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </>
                    )}
                    {onClose && (
                        <Tooltip title="Fermer la transcription">
                            <IconButton size="small" onClick={onClose} sx={{ color: 'grey.400', p: 0.5 }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Scrollable content body */}
            {(!isMobile || mobileHeight !== 'peek') && (
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 2,
                        pb: isMobile ? 3 : 2,
                        pt: 0.5,
                        touchAction: 'pan-y',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                    }}
                >
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loading && !act && (
                        <Typography variant="body2" sx={{ color: 'grey.400', py: 1 }}>
                            Pas de transcription pour ce document.
                        </Typography>
                    )}
                    {act && (
                        <>
                            {act.summary_fr && (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'grey.300', mb: 1.5, lineHeight: 1.45 }}>
                                    {act.summary_fr}
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                                {act.languages.map((lang) => (
                                    <Chip key={lang} size="small" label={lang} variant="outlined" sx={{ color: 'grey.300', borderColor: 'grey.700', fontSize: 11, height: 22 }} />
                                ))}
                                {act.legibility && (
                                    <Chip size="small" label={LEGIBILITY_LABELS[act.legibility] ?? act.legibility} variant="outlined" sx={{ color: 'grey.300', borderColor: 'grey.700', fontSize: 11, height: 22 }} />
                                )}
                                {orderedZones.length > 0 && (
                                    <Chip size="small" label={`${orderedZones.length} zones`} variant="outlined" sx={{ color: 'grey.400', borderColor: 'grey.800', fontSize: 11, height: 22 }} />
                                )}
                            </Box>

                            {translation && (
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={translated ? 'translation' : 'original'}
                                    onChange={(_, value) => value && setShowOriginal(value === 'original')}
                                    sx={{ mb: 1.5, '& .MuiToggleButton-root': { color: 'grey.400', borderColor: 'grey.700', py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none' }, '& .Mui-selected': { color: 'grey.100', bgcolor: 'rgba(255, 255, 255, 0.12)' } }}
                                >
                                    <ToggleButton value="translation">Traduction ({LANGUAGE_LABELS[translation.language] ?? translation.language})</ToggleButton>
                                    <ToggleButton value="original">Original</ToggleButton>
                                </ToggleButtonGroup>
                            )}

                            {orderedZones.map(({ zone, index }) => (
                                <Box
                                    key={index}
                                    onClick={() => onHoverZone(activeZone === index ? null : index)}
                                    onMouseEnter={() => onHoverZone(index)}
                                    onMouseLeave={() => onHoverZone(null)}
                                    sx={{
                                        borderLeft: `4px solid ${zoneColour(zone)}`,
                                        bgcolor: activeZone === index ? 'rgba(255, 255, 255, 0.14)' : 'rgba(255, 255, 255, 0.04)',
                                        borderRadius: 1,
                                        px: 1.25,
                                        py: 0.75,
                                        mb: 1.25,
                                        cursor: 'pointer',
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                                        <Typography variant="caption" sx={{ color: zoneColour(zone), fontWeight: 600, fontSize: 11 }}>
                                            {ZONE_LABELS[zone.type] ?? zone.type}{zone.uncertain ? ' · lecture incertaine' : ''}{translated && translation?.zones[String(index)] ? ' · traduit' : ''}
                                        </Typography>
                                        {activeZone === index && (
                                            <Typography variant="caption" sx={{ color: 'grey.400', fontSize: 10 }}>
                                                sélectionné
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography
                                        component="pre"
                                        variant="body2"
                                        sx={{
                                            m: 0,
                                            whiteSpace: 'pre-wrap',
                                            // A translation is prose; the transcription keeps its line layout.
                                            fontFamily: translated && translation?.zones[String(index)] ? 'inherit' : 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                            fontSize: isMobile ? 13.5 : 13,
                                            lineHeight: 1.5,
                                            color: 'grey.100',
                                        }}
                                    >
                                        {zoneText(zone, index)}
                                    </Typography>
                                </Box>
                            ))}

                            {act.pages.length > 1 && fullText && (
                                <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                    <Box
                                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', color: 'grey.300', py: 0.5 }}
                                        onClick={() => setFullTextOpen((open) => !open)}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {translated ? "Traduction complète de l'acte" : "Texte complet de l'acte"} ({act.pages.length} pages)
                                        </Typography>
                                        <IconButton size="small" sx={{ color: 'inherit', transform: fullTextOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}>
                                            <ExpandMoreIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Collapse in={fullTextOpen}>
                                        <Typography
                                            component="pre"
                                            variant="body2"
                                            sx={{
                                                mt: 1,
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                color: 'grey.200',
                                                bgcolor: 'rgba(0,0,0,0.3)',
                                                p: 1.5,
                                                borderRadius: 1,
                                            }}
                                        >
                                            {fullText}
                                        </Typography>
                                    </Collapse>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}
